import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  IconBrain,
  IconSend,
  IconHistory,
  IconChevronDown,
  IconCheck,
  IconRefresh,
  IconAlertTriangle,
  IconInfoCircle,
  IconX,
  IconRobot,
} from "@tabler/icons-react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Dropdown,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import useAgentTesting from "../../../services/AgentTestService";
import { listBasicAgents } from "../../../services/AgentsServices";
import {
  GetAgentVersions,
} from "../../../services/WaBottyServices";

// ==========================================
// FUNÇÕES AUXILIARES PARA QUEBRA DE MENSAGENS
// ==========================================

/**
 * Processa mensagens que contêm URLs de imagem e as divide em partes separadas
 */
const processImageBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string")
    return [{ type: "text", content: messageText }];

  const imageUrlRegex = /(https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|bmp|svg))/gi;
  const imageUrls = messageText.match(imageUrlRegex);

  if (!imageUrls || imageUrls.length === 0) {
    return [{ type: "text", content: messageText }];
  }

  const parts = [];
  let remainingText = messageText;

  imageUrls.forEach((imageUrl) => {
    const splitResult = remainingText.split(imageUrl);

    if (splitResult[0].trim()) {
      parts.push({
        type: "text",
        content: splitResult[0].trim(),
      });
    }

    parts.push({
      type: "image",
      content: imageUrl,
    });

    remainingText = splitResult.slice(1).join(imageUrl);
  });

  if (remainingText.trim()) {
    parts.push({
      type: "text",
      content: remainingText.trim(),
    });
  }

  return parts;
};

/**
 * Processa mensagens que contêm a tag [[break]] e as divide em partes separadas
 */
const processMessageBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string") return [messageText];

  if (!messageText.includes("[[break]]")) {
    return [messageText];
  }

  return messageText
    .split("[[break]]")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

/**
 * Processa todas as quebras ([[break]] + imagens)
 */
const processAllBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string")
    return [{ type: "text", content: messageText }];

  const breakParts = processMessageBreaks(messageText);
  const allParts = [];

  breakParts.forEach((part) => {
    const imageParts = processImageBreaks(part);
    allParts.push(...imageParts);
  });

  return allParts;
};

/**
 * Simula digitação com delay baseado no tamanho do texto
 */
const getTypingDelay = (content, contentType = "text") => {
  const baseDelay = 800;

  if (contentType === "image") {
    return 1200;
  }

  const charDelay = content.length * 25;
  const maxDelay = 2500;

  return Math.min(baseDelay + charDelay, maxDelay);
};

/**
 * Promessa para simular delay
 */
const simulateTyping = (duration) => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

// ==========================================
// COMPONENTE PRINCIPAL REFATORADO
// ==========================================

const AgentChat = ({ agentData, onClose }) => {
  const { user, bottyData } = useContext(UserContext);
  const {
    sendMessage,
    error: serviceError,
    setError,
  } = useAgentTesting();

  // Estados principais
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Estados para agentes e seleção
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(agentData || null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [showAgentSelector, setShowAgentSelector] = useState(false);

  // Estados para versões e configurações
  const [agentVersions, setAgentVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ==========================================
  // CARREGAMENTO DE AGENTES
  // ==========================================

  const loadAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const response = await listBasicAgents({ limit: 100 });
      
      if (response.success) {
        // Filtrar apenas agentes ativos
        const activeAgents = response.agents.filter(agent => agent.is_active);
        setAgents(activeAgents);
      } else {
        console.error('Erro ao carregar agentes:', response.error);
        setAgents([]);
      }
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // ==========================================
  // VALIDAÇÕES CRÍTICAS DO AGENTE
  // ==========================================

  const agentValidation = useMemo(() => {
    if (!selectedAgent) {
      return {
        isValid: false,
        errors: ["Nenhum agente selecionado"],
        warnings: [],
        canTest: false,
      };
    }

    const errors = [];
    const warnings = [];

    // Validações críticas
    if (!selectedAgent.id) {
      errors.push("ID do agente não encontrado");
    }

    // Para agentes da lista básica, não verificamos API key pois não está incluída
    // if (!selectedAgent.api_key || selectedAgent.api_key.trim() === "") {
    //   errors.push("API Key não configurada");
    // }

    if (!selectedAgent.is_active) {
      warnings.push("Agente está inativo");
    }

    if (!selectedAgent.agent_name || selectedAgent.agent_name.trim() === "") {
      warnings.push("Nome do agente não definido");
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      canTest: errors.length === 0 && selectedAgent.is_active,
    };
  }, [selectedAgent]);

  // ==========================================
  // SISTEMA DE AUTO-FOCUS
  // ==========================================

  const focusInput = useCallback(() => {
    if (!inputRef.current) return;
    if (isInitializing) return;
    if (!agentValidation.canTest) return;

    try {
      inputRef.current.focus();
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    } catch (error) {
      console.warn("⚠️ Erro ao focar input:", error);
    }
  }, [isInitializing, agentValidation.canTest]);

  useEffect(() => {
    if (!isInitializing && agentValidation.canTest) {
      const timer = setTimeout(() => {
        focusInput();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isInitializing, agentValidation.canTest, focusInput]);

  useEffect(() => {
    if (!isLoading && !isInitializing && agentValidation.canTest) {
      const timer = setTimeout(() => {
        focusInput();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isInitializing, agentValidation.canTest, focusInput]);

  // ==========================================
  // INICIALIZAÇÃO DO AGENTE ESPECÍFICO
  // ==========================================

  useEffect(() => {
    if (selectedAgent && selectedAgent.id) {
      initializeAgentForTesting();
    } else {
      setIsInitializing(false);
    }
  }, [selectedAgent, initializeAgentForTesting]);

  const initializeAgentForTesting = useCallback(async () => {
    setIsInitializing(true);
    setError(null);

    try {
      console.log("🚀 Inicializando teste para agente:", selectedAgent.agent_name);
      
      if (!agentValidation.isValid) {
        throw new Error(
          `Agente não pode ser testado: ${agentValidation.errors.join(", ")}`
        );
      }

      // Para agentes da lista básica, usamos dados limitados
      let versions = [];

      // Tentar buscar versões se possível
      try {
        const versionData = await GetAgentVersions(selectedAgent.id);
        versions = versionData.versions || [];
      } catch (error) {
        console.warn("⚠️ Erro ao buscar versões, usando versão padrão");
        versions = [
          {
            version: 1,
            is_active: true,
            is_current: true,
            label: "v.1",
            id: selectedAgent.id,
            created_at: selectedAgent.updated_at || new Date().toISOString(),
          },
        ];
      }

      // Configurar estados
      setAgentVersions(versions);
      setValidationResult(agentValidation);

      // Definir versão selecionada (ativa por padrão)
      const activeVersion = versions.find((v) => v.is_active) || versions[0];
      setSelectedVersion(activeVersion);

      // Mensagem de boas-vindas específica do agente
      const welcomeMessage = createWelcomeMessage(selectedAgent, agentValidation);
      setMessages([welcomeMessage]);

      console.log("✅ Agente inicializado com sucesso para testes");
    } catch (error) {
      console.error("❌ Erro ao inicializar agente:", error);

      const errorMessage = {
        id: Date.now(),
        text: `❌ Erro de inicialização: ${error.message}`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
        isSystem: true,
        messageType: "text",
      };
      setMessages([errorMessage]);
    } finally {
      setIsInitializing(false);
    }
  }, [selectedAgent, agentValidation, setError]);

  const createWelcomeMessage = (agent, validation) => {
    let welcomeText = "";

    if (validation.errors.length > 0) {
      welcomeText = `⚠️ Agente "${agent.agent_name}" carregado com ${validation.errors.length} erros críticos. Teste não disponível.`;
    } else if (validation.warnings.length > 0) {
      welcomeText = `⚠️ Agente "${agent.agent_name}" carregado com ${validation.warnings.length} avisos. Teste disponível com limitações.`;
    } else {
      welcomeText = `✅ Agente "${agent.agent_name}" carregado e pronto para testes!`;
    }

    return {
      id: Date.now(),
      text: welcomeText,
      isUser: false,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      isSystem: true,
      messageType: "text",
    };
  };

  // ==========================================
  // SCROLL
  // ==========================================

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    setTimeout(() => scrollToBottom(), 100);
  }, [messages, scrollToBottom]);

  // ==========================================
  // PROCESSAMENTO DE MENSAGENS MÚLTIPLAS
  // ==========================================

  const processMultiPartMessage = async (messageParts) => {
    for (let i = 0; i < messageParts.length; i++) {
      const part = messageParts[i];
      const content = part.content || part;
      const contentType = part.type || "text";

      if (i > 0) {
        const delay = getTypingDelay(content, contentType);
        await simulateTyping(delay);
      }

      const aiMessage = {
        id: Date.now() + i + 1 + Math.random() * 1000,
        text: content,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isPartOf: messageParts.length > 1,
        partIndex: i + 1,
        totalParts: messageParts.length,
        messageType: contentType,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  // ==========================================
  // RENDERIZAÇÃO DE CONTEÚDO
  // ==========================================

  const renderMessageContent = (text, messageType = "text") => {
    if (typeof text !== "string") return null;

    const cleanText = text.replace(/\[\[break\]\]/g, "").trim();

    if (messageType === "image") {
      return (
        <div className="d-block w-100 my-3">
          <img
            src={cleanText}
            alt="Imagem"
            className="img-fluid rounded"
            style={{
              maxWidth: "250px",
              maxHeight: "200px",
              objectFit: "cover",
            }}
            onError={(e) => {
              e.target.outerHTML = `
                <div class="text-muted p-3 bg-light border rounded" style="max-width: 250px;">
                  <small>📷 Imagem não pôde ser carregada</small><br/>
                  <a href="${cleanText}" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                    Ver imagem original
                  </a>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    if (cleanText.startsWith("audio:")) {
      const audioUrl = cleanText.split("audio:")[1];
      return (
        <audio controls className="w-100 mt-2">
          <source src={audioUrl} type="audio/wav" />
          Seu navegador não suporta o elemento de áudio.
        </audio>
      );
    }

    return (
      <span>
        {cleanText.split("\n").map((line, index) => (
          <span key={index}>
            {line}
            {index < cleanText.split("\n").length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };

  // ==========================================
  // SELEÇÃO DE AGENTE
  // ==========================================

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setShowAgentSelector(false);
    setMessages([]);
    setError(null);
  };

  // ==========================================
  // ENVIO DE MENSAGENS ESPECÍFICAS DO AGENTE
  // ==========================================

  const handleSendMessage = async () => {
    if (
      !inputMessage.trim() ||
      isLoading ||
      !selectedVersion ||
      !agentValidation.canTest ||
      isInitializing
    )
      return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      messageType: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      console.log(
        `📤 Enviando mensagem para agente ${selectedAgent.agent_name}:`,
        inputMessage
      );

      // Payload específico do agente
      const messagePayload = {
        id: selectedAgent.id,
        uid: bottyData.uid,
        phone: user.phone,
        message: inputMessage,
        account_id: user.account_id,
        agent_id: selectedAgent.id,
        version_active: selectedVersion.version || 1,
        internal: true,
      };

      const response = await sendMessage(messagePayload);

      if (response) {
        let responseText = "";
        if (response.status === 200 && response.data) {
          responseText = response.data;
        } else if (response.message) {
          responseText = response.message;
        } else {
          responseText = "";
        }

        console.log(
          `📥 Resposta do agente ${selectedAgent.agent_name}:`,
          responseText
        );

        const messageParts = processAllBreaks(responseText);

        if (messageParts.length === 1 && messageParts[0].type === "text") {
          const aiMessage = {
            id: Date.now() + 1,
            text: messageParts[0].content,
            isUser: false,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            messageType: "text",
          };
          setMessages((prev) => [...prev, aiMessage]);
        } else {
          await processMultiPartMessage(messageParts);
        }
      } else {
        throw new Error(serviceError || "Erro na comunicação com o agente");
      }
    } catch (error) {
      console.error(
        `❌ Erro ao comunicar com agente ${selectedAgent.agent_name}:`,
        error
      );

      let errorText = `Falha na comunicação com ${selectedAgent.agent_name}`;

      if (error.response?.data?.error) {
        errorText = error.response.data.error;
      } else if (serviceError) {
        errorText = serviceError;
      } else if (error.message) {
        errorText = error.message;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: `❌ Erro: ${errorText}`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
        messageType: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ==========================================
  // GERENCIAMENTO DE VERSÕES ESPECÍFICAS
  // ==========================================

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    setShowVersionSelector(false);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: `✅ Agora testando ${
          version.is_current ? "versão atual" : `versão ${version.version}`
        } do agente ${selectedAgent.agent_name}.`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isSystem: true,
        messageType: "text",
      },
    ]);

    setTimeout(() => {
      focusInput();
    }, 200);
  };

  const clearConversation = async () => {
    setMessages([]);
    setError(null);

    if (selectedVersion && agentValidation.canTest) {
      try {
        await sendMessage({
          uid: bottyData.uid,
          phone: user.phone,
          message: "resetar",
          account_id: user.account_id,
          agent_id: selectedAgent.id,
          internal: true,
        });
      } catch (error) {
        console.warn("⚠️ Erro ao resetar conversa:", error);
      }

      const welcomeMessage = createWelcomeMessage(selectedAgent, agentValidation);
      setMessages([welcomeMessage]);
    }

    setTimeout(() => {
      focusInput();
    }, 300);
  };

  // ==========================================
  // RENDERIZAÇÃO DE COMPONENTES
  // ==========================================

  const renderMessageBubble = (message) => {
    const hasImage = message.messageType === "image";
    
    return (
      <div
        key={message.id}
        className={`d-flex mb-3 ${message.isUser ? "justify-content-end" : "justify-content-start"}`}
      >
        <div
          className={`p-3 rounded-3 ${
            message.isError
              ? "bg-danger text-white"
              : message.isSystem
              ? "bg-info text-white"
              : message.isUser
              ? "bg-primary text-white"
              : "bg-white border"
          }`}
          style={{ maxWidth: "70%" }}
        >
          {hasImage ? (
            <div className="w-100">
              {renderMessageContent(message.text, message.messageType)}
            </div>
          ) : (
            <div className={message.isError || message.isSystem ? "" : ""}>
              {renderMessageContent(message.text, message.messageType)}
            </div>
          )}

          <div className={`mt-2 ${message.isUser ? "text-end" : "text-start"}`}>
            <small className={`text-${message.isUser || message.isError || message.isSystem ? "light" : "muted"}`}>
              {message.timestamp}
              {message.isPartOf && (
                <span className="ms-2">
                  <Badge bg="secondary" className="ms-1">
                    {message.partIndex}/{message.totalParts}
                    {message.messageType === "image" && " 🖼️"}
                  </Badge>
                </span>
              )}
            </small>
          </div>
        </div>
      </div>
    );
  };

  const renderValidationAlert = () => {
    if (
      !validationResult ||
      (!validationResult.errors.length && !validationResult.warnings.length)
    ) {
      return null;
    }

    return (
      <div className="mb-3">
        {validationResult.errors.length > 0 && (
          <Alert variant="danger">
            <strong>❌ Erros críticos:</strong>
            <ul className="mb-0 mt-2">
              {validationResult.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {validationResult.warnings.length > 0 && (
          <Alert variant="warning">
            <strong>⚠️ Avisos:</strong>
            <ul className="mb-0 mt-2">
              {validationResult.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    );
  };

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================

  return (
    <Container fluid className="p-0" style={{ height: "100vh" }}>
      <Row className="g-0 h-100">
        <Col>
          <Card className="h-100 border-0 rounded-0">
            {/* Header */}
            <Card.Header className="bg-primary text-white py-3">
              <Row className="align-items-center">
                <Col className="d-flex align-items-center">
                  <div 
                    className="bg-white bg-opacity-25 rounded-circle p-2 me-3"
                  >
                    <IconBrain size={24} />
                  </div>
                  <div>
                    <h5 className="mb-0">
                      {selectedAgent ? selectedAgent.agent_name : "Selecione um Agente"}
                    </h5>
                    <small className="text-white-50">
                      {selectedAgent ? selectedAgent.agent_job || "Cargo não definido" : "Teste de Agentes IA"}
                    </small>
                  </div>
                </Col>
                <Col xs="auto">
                  <div className="d-flex gap-2">
                    {/* Seletor de Agente */}
                    <Dropdown show={showAgentSelector} onToggle={setShowAgentSelector}>
                      <Dropdown.Toggle 
                        variant="outline-light"
                        size="sm"
                        className="d-flex align-items-center"
                        disabled={loadingAgents}
                      >
                        {loadingAgents ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <IconRobot size={16} className="me-1" />
                        )}
                        Agente
                        <IconChevronDown size={14} className="ms-1" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Header>Selecionar Agente</Dropdown.Header>
                        {agents.length === 0 ? (
                          <Dropdown.Item disabled>Nenhum agente disponível</Dropdown.Item>
                        ) : (
                          agents.map((agent) => (
                            <Dropdown.Item
                              key={agent.id}
                              onClick={() => handleAgentSelect(agent)}
                              active={selectedAgent?.id === agent.id}
                            >
                              <div>
                                <div className="fw-semibold">{agent.agent_name}</div>
                                <small className="text-muted">
                                  {agent.agent_job || "Sem cargo definido"}
                                </small>
                              </div>
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Seletor de Versão */}
                    {selectedAgent && agentVersions.length > 0 && (
                      <Dropdown show={showVersionSelector} onToggle={setShowVersionSelector}>
                        <Dropdown.Toggle 
                          variant="outline-light"
                          size="sm"
                          disabled={!agentValidation.canTest}
                        >
                          <IconHistory size={16} className="me-1" />
                          {selectedVersion?.is_current
                            ? `v${selectedVersion.version || 1} (atual)`
                            : `v${selectedVersion?.version || 1}`}
                          <IconChevronDown size={14} className="ms-1" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Header>Versões Disponíveis</Dropdown.Header>
                          {agentVersions.map((version, index) => (
                            <Dropdown.Item
                              key={index}
                              onClick={() => handleVersionSelect(version)}
                              active={selectedVersion?.version === version.version}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="fw-semibold">
                                    v{version.version}
                                    {version.is_current && (
                                      <Badge bg="success" className="ms-2">Atual</Badge>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    {new Date(version.created_at).toLocaleDateString()}
                                  </small>
                                </div>
                                {selectedVersion?.version === version.version && (
                                  <IconCheck size={16} className="text-primary" />
                                )}
                              </div>
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}

                    {/* Botão Limpar */}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Limpar conversa</Tooltip>}
                    >
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={clearConversation}
                        disabled={!agentValidation.canTest}
                      >
                        <IconRefresh size={16} />
                      </Button>
                    </OverlayTrigger>

                    {/* Botão Fechar */}
                    {onClose && (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip>Fechar</Tooltip>}
                      >
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={onClose}
                        >
                          <IconX size={16} />
                        </Button>
                      </OverlayTrigger>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Info Bar */}
              {selectedAgent && selectedVersion && (
                <Row className="mt-2">
                  <Col>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge bg="light" text="dark" className="px-2 py-1">
                        <strong>Testando:</strong> {" "}
                        {selectedVersion.is_current
                          ? "Versão atual"
                          : `Versão ${selectedVersion.version}`}
                      </Badge>
                      
                      {validationResult?.errors.length > 0 && (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip>
                              {validationResult.errors.length} erro(s) encontrado(s)
                            </Tooltip>
                          }
                        >
                          <Badge bg="danger" className="px-2 py-1">
                            <IconAlertTriangle size={12} className="me-1" />
                            {validationResult.errors.length}
                          </Badge>
                        </OverlayTrigger>
                      )}
                      
                      {validationResult?.warnings.length > 0 && (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip>
                              {validationResult.warnings.length} aviso(s)
                            </Tooltip>
                          }
                        >
                          <Badge bg="warning" className="px-2 py-1">
                            <IconInfoCircle size={12} className="me-1" />
                            {validationResult.warnings.length}
                          </Badge>
                        </OverlayTrigger>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Header>

            {/* Alertas de validação */}
            {renderValidationAlert()}

            {/* Área de Mensagens */}
            <Card.Body 
              className="p-3 overflow-auto flex-grow-1"
              style={{ 
                backgroundImage: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                minHeight: "400px"
              }}
            >
              {isInitializing ? (
                <div className="text-center py-5">
                  <Spinner className="mb-3" />
                  <h5 className="text-muted">Inicializando agente...</h5>
                  <p className="text-muted mb-0">
                    Preparando ambiente de testes
                  </p>
                </div>
              ) : !selectedAgent ? (
                <div className="text-center py-5">
                  <IconRobot size={64} className="text-muted mb-3" />
                  <h5 className="text-muted">Nenhum agente selecionado</h5>
                  <p className="text-muted mb-0">
                    Selecione um agente para começar os testes
                  </p>
                </div>
              ) : (
                <>
                  {messages.map((message) => renderMessageBubble(message))}

                  {isLoading && (
                    <div className="d-flex justify-content-start mb-3">
                      <div className="bg-white border p-3 rounded-3" style={{ maxWidth: "70%" }}>
                        <div className="d-flex align-items-center">
                          <Spinner size="sm" className="me-2" />
                          <div className="text-muted">
                            <strong>{selectedAgent.agent_name}</strong> está digitando...
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </Card.Body>

            {/* Área de Input */}
            <Card.Footer className="bg-white border-top p-3">
              <Row className="align-items-end">
                <Col>
                  <Form.Control
                    as="textarea"
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      agentValidation.canTest
                        ? `Teste o agente ${selectedAgent?.agent_name || ""}...`
                        : "Selecione um agente ativo para começar"
                    }
                    rows={1}
                    disabled={!agentValidation.canTest || isLoading || isInitializing}
                    style={{ 
                      resize: "none",
                      borderRadius: "20px",
                      border: "2px solid #dee2e6"
                    }}
                    className="bg-light"
                  />
                </Col>
                <Col xs="auto">
                  <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    disabled={
                      !inputMessage.trim() ||
                      isLoading ||
                      !agentValidation.canTest ||
                      isInitializing
                    }
                    className="rounded-circle"
                    style={{ width: "40px", height: "40px" }}
                    title={
                      !agentValidation.canTest
                        ? "Selecione um agente ativo primeiro"
                        : "Enviar mensagem"
                    }
                  >
                    <IconSend size={18} />
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AgentChat;